import React, { useEffect, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiCurrencyLine } from 'react-icons/ri'

import {
  Card,
  FlexView,
  Table,
  Spinner,
  Link,
  Modal,
} from '../components/common'
import PiecesPieChartCard from 'components/safety/PiecesPieChartCard'
import ApplicationPieChartCard from 'components/safety/ApplicationPieChartCard'
import PiecesByMaterialChartCard from 'containers/safety/PiecesByMaterialChartCard'
import styled from 'styled-components'
import { SafetyStockContext } from 'stores/SafetyStockStore'
import { UserContext } from 'stores/UserStore'
import { AppContext } from 'stores/AppStore'

const Grid = styled(FlexView)`
  flex: 1;
  width: 100%;
  display: grid;
  align-items: stretch;
  grid-template-columns: 25% 25% 50%;
  max-height: 400px;

  @media screen and (max-width: 700px) {
    grid-template-columns: 100%;
  }
`

const Safety = () => {
  const DEFAULT_URL = 'safety-stock/home'
  const { registryLog } = useContext(UserContext)
  const { setPageSubtitle } = useContext(AppContext)
  const { t } = useTranslation()
  const [isOpen, setOpen] = useState(false)
  const {
    getSafetyStockData,
    tableData,
    chartPiecesByCustomerData,
    chartPiecesByApplicationData,
    isLoading,
    extractTableData,
    getSafetyStockExtractData,
    isLoadingModal,
  } = useContext(SafetyStockContext)

  useEffect(() => {
    //registryLogOld('client_in', DEFAULT_URL)
    registryLog(DEFAULT_URL, 'navigation', 'page_in')
    getSafetyStockData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPageSubtitle(t('safety.pageTitle'))
  }, [setPageSubtitle, t])

  const toggleModal = () => {
    // registryLog('click_modalExtract', DEFAULT_URL)

    setOpen((current) => !current)
  }

  const formatNm = (nm) => {
    return nm ? nm.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''
  }

  const getDetails = (nm) => () => {
    if (nm) {
      //registryLogOld('click_modalExtract', DEFAULT_URL)
      registryLog(DEFAULT_URL, 'click', 'modal_extract')
      getSafetyStockExtractData(nm)
      toggleModal()
    }
  }

  const columns = [
    {
      Header: t('safety.table.contract'),
      accessor: 'contract',
      id: 'contract',
    },
    {
      Header: t('safety.table.dscCustomer'),
      accessor: 'dscCustomer',
      id: 'dscCustomer',
    },
    {
      Header: t('safety.table.nm'),
      accessor: (row) => <div> {formatNm(row.nm)}</div>,
      id: 'nm',
    },
    {
      Header: t('safety.table.description'),
      accessor: 'dscMaterial',
      id: 'dscMaterial',
    },
    {
      Header: t('safety.table.qtyPieces'),
      accessor: 'qty',
      id: 'qty',
    },
    {
      Header: t('safety.table.qtyPiecesPlan'),
      accessor: 'qtyPlan',
      id: 'qtyPlan',
    },
    {
      Header: t('safety.table.plant'),
      accessor: 'dscPlant',
      id: 'dscPlant',
    },
    {
      Header: t('safety.table.others'),
      accessor: (row) => (
        <Link onClick={getDetails(row.nm)}>
          <RiCurrencyLine />
        </Link>
      ),
      id: 'others',
    },
  ]

  const extractColumns = [
    {
      Header: t('safety.table.nm'),
      accessor: 'nm',
      id: 'nm',
    },
    {
      Header: t('safety.table.qtyPiecesOut'),
      accessor: 'qtyOut',
      id: 'qtyOut',
    },
    {
      Header: t('safety.table.qtyPiecesPlan'),
      accessor: 'qtyPlan',
      id: 'qtyPlan',
    },
    {
      Header: t('safety.table.dateOut'),
      accessor: (row) =>
        new Date(row.dateOut).toLocaleDateString('pt-br', { timeZone: 'utc' }),
      // accessor: row => moment(row.dateOut).format('DD/MM/YYYY'),
      id: 'dateOut',
    },
    {
      Header: t('safety.table.dateReplacement'),
      accessor: (row) =>
        new Date(row.dateReplacement).toLocaleDateString('pt-br', {
          timeZone: 'utc',
        }),
      // accessor: row => moment(row.dateReplacement).format('DD/MM/YYYY'),
      id: 'dateReplacement',
    },
  ]

  return (
    <>
      <Grid>
        <PiecesPieChartCard
          isLoading={isLoading}
          title={t('safety.chart.piecesByCustomer')}
          pieces={chartPiecesByCustomerData}
          flex="1"
        />
        <ApplicationPieChartCard
          isLoading={isLoading}
          title={t('safety.chart.piecesByApplication')}
          pieces={chartPiecesByApplicationData}
          flex="1"
        />
        <PiecesByMaterialChartCard flex="1" />
      </Grid>

      <Card alignSelf="stretch" padding="8px 16px" flex="1">
        {isLoading ? (
          <FlexView
            flex="1"
            width="100%"
            height="100%"
            alignItems="center"
            padding="50px"
            fontSize="big"
            fontWeight="bold"
            color="gray"
            backgroundColor="transparent"
          >
            <Spinner />
          </FlexView>
        ) : (
          <Table
            width="100%"
            columns={columns}
            data={tableData}
            sortable
            elevation="none"
            borderRadius="card"
            paginated
            margin="8px 16px"
            style={{ whiteSpace: 'nowrap' }}
          />
        )}
      </Card>
      <Modal isOpen={isOpen} onOutsideClick={toggleModal}>
        {isLoadingModal ? (
          <FlexView
            flex="1"
            width="100%"
            height="100%"
            alignItems="center"
            padding="50px"
            fontSize="big"
            fontWeight="bold"
            color="gray"
            backgroundColor="transparent"
          >
            <Spinner />
          </FlexView>
        ) : (
          <Card
            flex="1"
            flexDirection="column"
            margin="0px 50px"
            padding="0px"
            alignItems="center"
            maxHeight="560px"
            style={{ overflow: 'auto' }}
          >
            {console.debug(extractTableData)}
            {!extractTableData ? (
              <FlexView
                margin="8px 0px 0px"
                fontSize="14"
                padding="4%"
                fontWeight="bold"
                color="gray"
              >
                {t('safety.table.noData')}
              </FlexView>
            ) : (
              <Table
                width="100%"
                margin="8px 0px 0px"
                columns={extractColumns}
                data={extractTableData}
                sortable
                elevation="none"
                borderRadius="card"
              />
            )}
          </Card>
        )}
      </Modal>
    </>
  )
}

export default Safety
